import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, collectionGroup } from 'firebase/firestore';
import { db } from '../firebase';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // For table formatting in PDF
import { updateUserAPI } from '../services/serverService'; // Ensures audit logging is triggered

const PaginatedList = ({ data, title, renderItem, emptyMessage }) => {
    const [displayCount, setDisplayCount] = useState(15);
    const displayedData = data.slice(0, displayCount);

    // Reset display count if data changes
    useEffect(() => {
        setDisplayCount(15);
    }, [data]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h3 style={{ fontSize: '16px', color: '#555', marginBottom: '15px' }}>{title}</h3>
            {data.length === 0 ? (
                <p style={{ color: '#6b7280' }}>{emptyMessage}</p>
            ) : (
                displayedData.map((item, index) => renderItem(item, index))
            )}
            
            {data.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                        Showing {displayedData.length} of {data.length} records
                    </span>
                    {displayCount < data.length && (
                        <button 
                            onClick={() => setDisplayCount(prev => prev + 15)}
                            style={{ 
                                padding: '8px 16px', 
                                backgroundColor: '#f1f5f9', 
                                color: '#334155',
                                border: '1px solid #e2e8f0', 
                                borderRadius: '6px', 
                                cursor: 'pointer', 
                                fontWeight: '600',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                        >
                            Load More
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * EmployeeRotaModule
 * 
 * Handles the nested "Rota" tab content inside the Employee Profile.
 * Contains: Rota Statistics, Visits, Shifts, Trainings, Meetings.
 */
export default function EmployeeRotaModule({ employee, serverId, canEdit }) {
    const [activeSubTab, setActiveSubTab] = useState('statistics');
    const [visits, setVisits] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [meetings, setMeetings] = useState([]);
    
    // Work Summary States
    const [scheduledHours, setScheduledHours] = useState(0);
    const [unscheduledHours, setUnscheduledHours] = useState(0);
    const [completedHours, setCompletedHours] = useState(0);
    const [activitiesCompleted, setActivitiesCompleted] = useState(0);
    const [totalCost, setTotalCost] = useState(0);

    const [clockInLogs, setClockInLogs] = useState([]);
    

    const [loading, setLoading] = useState(true);

    const [leavesData, setLeavesData] = useState(
        (employee.employeeDetails && employee.employeeDetails.leaves) ? employee.employeeDetails.leaves : {}
    );
    const [isEditingLeaves, setIsEditingLeaves] = useState(false);

    useEffect(() => {
        if (!serverId || !employee?.id) return;
        fetchAllRotaData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId, employee]);

    const fetchAllRotaData = async () => {
        setLoading(true);
        try {
            // 1. Fetch Visits
            const visitsRef = collection(db, 'servers', serverId, 'allVisits');
            const visitsQ = query(visitsRef, where('staffIDs', 'array-contains', employee.id));
            const visitsSnap = await getDocs(visitsQ);
            const fetchedVisits = visitsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Sort visits locally descending by datetime
            fetchedVisits.sort((a, b) => {
                const dateA = a.datetime?.toDate?.() || new Date(a.visit_start_date || 0);
                const dateB = b.datetime?.toDate?.() || new Date(b.visit_start_date || 0);
                return dateB - dateA;
            });
            setVisits(fetchedVisits);

            // 2. Fetch Shifts
            const shiftsRef = collection(db, 'servers', serverId, 'employeeShifts');
            const shiftsQ = query(shiftsRef, where('staffID', '==', employee.id));
            const shiftsSnap = await getDocs(shiftsQ);
            const fetchedShifts = shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Sort shifts locally descending
            fetchedShifts.sort((a, b) => {
                const dateA = a.shiftStartDate ? new Date(a.shiftStartDate) : new Date(0);
                const dateB = b.shiftStartDate ? new Date(b.shiftStartDate) : new Date(0);
                return dateB - dateA;
            });
            setShifts(fetchedShifts);

            // 3. Fetch Trainings
            const trainingsRef = collection(db, 'servers', serverId, 'employeeTrainings');
            const trainingsQ = query(trainingsRef, where('staffID', '==', employee.id));
            const trainingsSnap = await getDocs(trainingsQ);
            const fetchedTrainings = trainingsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            fetchedTrainings.sort((a, b) => {
                const dateA = a.date ? new Date(a.date) : new Date(0);
                const dateB = b.date ? new Date(b.date) : new Date(0);
                return dateB - dateA;
            });
            setTrainings(fetchedTrainings);

            // 4. Fetch Meetings
            const meetingsRef = collection(db, 'servers', serverId, 'employeeMeetings');
            const meetingsQ = query(meetingsRef, where('staffID', '==', employee.id));
            const meetingsSnap = await getDocs(meetingsQ);
            const fetchedMeetings = meetingsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            fetchedMeetings.sort((a, b) => {
                const dateA = a.date ? new Date(a.date) : new Date(0);
                const dateB = b.date ? new Date(b.date) : new Date(0);
                return dateB - dateA;
            });
            setMeetings(fetchedMeetings);

            // --- CALCULATE WORK SUMMARY STATISTICS ---
            calculateWorkSummary(fetchedVisits, fetchedShifts);

            // 5. Fetch Clock-in Logs
            const clockingRef = collectionGroup(db, 'staffVisitClockingLogs');
            const clockingQ = query(clockingRef, where('serverID', '==', serverId), where('staffID', '==', employee.username));
            const clockingSnap = await getDocs(clockingQ);
            const fetchedClocking = clockingSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            fetchedClocking.sort((a, b) => {
                const dateA = a.visitStarted ? new Date(a.visitStarted) : new Date(0);
                const dateB = b.visitStarted ? new Date(b.visitStarted) : new Date(0);
                return dateB - dateA;
            });
            setClockInLogs(fetchedClocking);

        } catch (error) {
            console.error("Error fetching rota data:", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Helper to parse time strings like "10:00 AM" or "14:00" and calculate total difference in hours
     */
    const calculateTimeDifference = (startTime, endTime) => {
        try {
            if (!startTime || !endTime) return 0;
            const parseTime = (timeStr) => {
                const match = timeStr.trim().match(/(\d+):(\d+)\s*(AM|PM)?/i);
                if (!match) return 0;
                let hours = parseInt(match[1], 10);
                const mins = parseInt(match[2], 10);
                const modifier = match[3];
                if (modifier && modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
                if (modifier && modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;
                return hours + (mins / 60);
            };
            const s = parseTime(startTime);
            const e = parseTime(endTime);
            const diff = e - s;
            return diff > 0 ? diff : diff + 24; // Handle overnight shifts
        } catch(e) {
            return 0;
        }
    };

    /**
     * calculateWorkSummary
     * Calculates the hours and activities from fetched shifts and visits
     */
    const calculateWorkSummary = (fetchedVisits, fetchedShifts) => {
        let schHours = 0, unschHours = 0, compHours = 0, activities = 0;

        fetchedShifts.forEach(shift => {
            const h = calculateTimeDifference(shift.shiftStartTime, shift.shiftEndTime);
            if (shift.shiftStatus === 'active') schHours += h;
            if (shift.shiftStatus === 'pending') unschHours += h;
            if (shift.shiftStatus === 'completed') compHours += h;
        });

        fetchedVisits.forEach(visit => {
            if (Array.isArray(visit.assignedActivities)) {
                activities += visit.assignedActivities.length;
            }
        });

        setScheduledHours(schHours);
        setUnscheduledHours(unschHours);
        setCompletedHours(compHours);
        setActivitiesCompleted(activities);

        const hourlyRate = employee.employeeDetails?.hourlyRate || 0;
        setTotalCost(hourlyRate * compHours);
    };

    /**
     * handleSaveLeaves
     */
    const handleSaveLeaves = async () => {
        try {
            await updateUserAPI(employee.id, {
                serverId: serverId,
                role: 'employee',
                'employeeDetails.leaves': leavesData
            });

            alert("Leaves updated successfully. The action has been logged in Audit Logs.");
            setIsEditingLeaves(false);
        } catch (error) {
            console.error("Error updating leaves:", error);
            alert("Failed to update leaves.");
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        
        doc.setFontSize(18);
        doc.text("Employee Rota Report", 14, 22);
        
        doc.setFontSize(12);
        doc.text(`Employee Name: ${employee.fullName || 'N/A'}`, 14, 32);
        doc.text(`Username: ${employee.username || 'N/A'}`, 14, 40);

        doc.setFontSize(14);
        doc.text("Rota Statistics", 14, 55);
        
        const statsBody = [
            ['Total Annual Leaves', leavesData.totalAnnualLeaves || 0],
            ['Remaining Annual Leaves', leavesData.annualLeaveRemaining || 0],
            ['Total Sick Leaves', leavesData.totalSickLeaves || 0],
            ['Paternity Leaves', leavesData.totalPaternityLeaves || 0],
            ['Maternity Leaves', leavesData.totalMaternityLeaves || 0],
            ['Bank Holidays', leavesData.rolloverDays || 0],
            ['Unauthorized Leaves', leavesData.totalUnauthorisedLeaves || 0]
        ];

        doc.autoTable({
            startY: 60,
            head: [['Metric', 'Value']],
            body: statsBody,
            theme: 'grid',
            headStyles: { fillColor: [0, 86, 179] }
        });

        const finalY = doc.lastAutoTable.finalY || 60;
        doc.text("Visit Schedule", 14, finalY + 15);
        
        const visitsBody = visits.map(v => [
            v.date || 'N/A', 
            v.serviceUserName || 'N/A', 
            v.status || 'N/A'
        ]);

        doc.autoTable({
            startY: finalY + 20,
            head: [['Date', 'Client', 'Status']],
            body: visitsBody,
            theme: 'striped',
            headStyles: { fillColor: [0, 86, 179] }
        });

        doc.save(`${employee.username}_Rota_Report.pdf`);
    };

    const handleLeaveChange = (field, value) => {
        setLeavesData(prev => ({ ...prev, [field]: Number(value) }));
    };

    const renderTabs = () => {
        const tabs = [
            { id: 'statistics', label: 'Rota Statistics' },
            { id: 'visits', label: 'Visits' },
            { id: 'shifts', label: 'Shifts' },
            { id: 'clockInLogs', label: 'Clock-in Logs' },
            { id: 'manageShiftTime', label: 'Manage Shift Time' },
            { id: 'trainings', label: 'Trainings' },
            { id: 'meetings', label: 'Meetings' }
        ];

        return (
            <div style={{ display: 'flex', borderBottom: '2px solid #eee', marginBottom: '20px', overflowX: 'auto' }}>
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setActiveSubTab(t.id)}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            background: 'none',
                            borderBottom: activeSubTab === t.id ? '3px solid #0056b3' : '3px solid transparent',
                            color: activeSubTab === t.id ? '#0056b3' : '#666',
                            fontWeight: activeSubTab === t.id ? 'bold' : 'normal',
                            cursor: 'pointer',
                            fontSize: '14px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
        );
    };

    const StatCard = ({ label, field, color }) => {
        const val = leavesData[field] || 0;
        return (
            <div style={{ 
                padding: '15px', 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                border: `1px solid ${color}33`, 
                borderLeft: `4px solid ${color}`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}>
                <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', fontWeight: 'bold' }}>{label}</span>
                {isEditingLeaves ? (
                    <input 
                        type="number" 
                        value={val}
                        onChange={(e) => handleLeaveChange(field, e.target.value)}
                        style={{ marginTop: '5px', padding: '5px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }}
                    />
                ) : (
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginTop: '5px' }}>{val}</span>
                )}
            </div>
        );
    };

    const renderRotaStatistics = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Work Summary Section */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0', display: 'flex', alignItems: 'center' }}>
                        Work Summary
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                        <div style={{ backgroundColor: '#eef2ff', borderRadius: '8px', padding: '16px' }}>
                            <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#4f46e5', fontWeight: '500' }}>Scheduled Hours</p>
                            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#312e81' }}>{scheduledHours.toFixed(1)} hrs</p>
                        </div>
                        <div style={{ backgroundColor: '#fffbeb', borderRadius: '8px', padding: '16px' }}>
                            <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#d97706', fontWeight: '500' }}>Unscheduled</p>
                            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#78350f' }}>{unscheduledHours.toFixed(1)} hrs</p>
                        </div>
                        <div style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', padding: '16px' }}>
                            <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#16a34a', fontWeight: '500' }}>Completed Hours</p>
                            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#14532d' }}>{completedHours.toFixed(1)} hrs</p>
                        </div>
                        <div style={{ backgroundColor: '#eff6ff', borderRadius: '8px', padding: '16px' }}>
                            <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#2563eb', fontWeight: '500' }}>Activities Done</p>
                            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1e3a8a' }}>{activitiesCompleted}</p>
                        </div>
                        <div style={{ backgroundColor: '#faf5ff', borderRadius: '8px', padding: '16px' }}>
                            <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#9333ea', fontWeight: '500' }}>Total Cost</p>
                            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#581c87' }}>£{totalCost.toFixed(2)}</p>
                        </div>
                    </div>
                </div>

                {/* Leaves Summary Section */}
                <div>
                    <h3 style={{ fontSize: '16px', color: '#555', marginBottom: '15px' }}>Leaves Summary</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px' }}>
                        <StatCard label="Total Annual" field="totalAnnualLeaves" color="#17a2b8" />
                        <StatCard label="Taken Leaves" field="takenLeaves" color="#dc3545" />
                        <StatCard label="Remaining Annual" field="annualLeaveRemaining" color="#28a745" />
                        <StatCard label="Sick Leaves" field="totalSickLeaves" color="#fd7e14" />
                        <StatCard label="Paternity" field="totalPaternityLeaves" color="#6f42c1" />
                        <StatCard label="Maternity" field="totalMaternityLeaves" color="#e83e8c" />
                        <StatCard label="Bank Holidays" field="rolloverDays" color="#6c757d" />
                        <StatCard label="Unauthorized" field="totalUnauthorisedLeaves" color="#dc3545" />
                    </div>
                </div>
            </div>
        );
    };

    if (loading) return <div style={{ padding: '20px' }}>Loading Rota Data...</div>;

    return (
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', color: '#2c3e50' }}>Schedule & Attendance</h2>
                <div>
                    <button 
                        onClick={handleExportPDF}
                        style={{ padding: '8px 15px', backgroundColor: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', marginRight: '10px' }}
                    >
                        Export PDF
                    </button>
                    {activeSubTab === 'statistics' && canEdit && (
                        <button 
                            onClick={() => isEditingLeaves ? handleSaveLeaves() : setIsEditingLeaves(true)}
                            style={{ padding: '8px 15px', backgroundColor: isEditingLeaves ? '#28a745' : '#0056b3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            {isEditingLeaves ? 'Save Changes' : 'Edit Leaves'}
                        </button>
                    )}
                </div>
            </div>

            {renderTabs()}

            {activeSubTab === 'statistics' && renderRotaStatistics()}

            {activeSubTab === 'visits' && (
                <PaginatedList
                    title="Assigned Visits"
                    data={visits}
                    emptyMessage="No visits found."
                    renderItem={(v, i) => (
                        <div key={i} style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', color: '#1f2937' }}>
                                    ⏱ Start Time: {v.timeRange?.from || 'N/A'} - End Time: {v.timeRange?.to || 'N/A'}
                                </div>
                                <span style={{
                                    padding: '4px 12px', borderRadius: '9999px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', color: 'white',
                                    backgroundColor: v.status?.toLowerCase() === 'completed' ? '#22c55e' : v.status?.toLowerCase() === 'cancelled' ? '#ef4444' : '#eab308'
                                }}>
                                    {v.status || 'UNKNOWN'}
                                </span>
                            </div>
                            <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                📅 Start Date: {v.visit_start_date || 'N/A'} - End Date: {v.visit_end_date || 'N/A'}
                            </div>
                            <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                📍 {typeof v.address === 'string' ? v.address : 
                                    (v.address ? [v.address.firstLineOfAddress, v.address.secondLineOfAddress, v.address.town, v.address.county, v.address.postalCode, v.address.country].filter(Boolean).join(', ') : 'Address not provided')
                                }
                            </div>
                            <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                👤 Visit For: {v.userID || 'Unknown User'}
                            </div>
                            {v.staffIDs && v.staffIDs.length > 0 && (
                                <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                    👥 Assign to: 
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: '8px' }}>
                                        {v.staffIDs.map((staff, idx) => (
                                            <span key={idx} style={{ backgroundColor: '#f3f4f6', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '500', color: '#374151' }}>{staff}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                />
            )}

            {activeSubTab === 'shifts' && (
                <PaginatedList
                    title="Shift Logs"
                    data={shifts}
                    emptyMessage="No shifts found."
                    renderItem={(s, i) => {
                        const hasBreak = s.isBreakAllowed;
                        return (
                            <div key={i} style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontWeight: 'bold', color: '#1f2937' }}>
                                        Start: {s.shiftStartDate ? new Date(s.shiftStartDate).toDateString() : 'N/A'} - End: {s.shiftEndDate ? new Date(s.shiftEndDate).toDateString() : 'N/A'}
                                    </div>
                                    <span style={{ padding: '4px 12px', borderRadius: '9999px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#166534' }}>
                                        {s.status || 'Approved'}
                                    </span>
                                </div>
                                <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⏱ {s.shiftStartTime || 'N/A'} - {s.shiftEndTime || 'N/A'}
                                </div>
                                {hasBreak && (
                                    <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        ☕ Break: {s.breakStartTime || 'N/A'} - {s.breakEndTime || 'N/A'}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
            )}

            {activeSubTab === 'trainings' && (
                <PaginatedList
                    title="Trainings"
                    data={trainings}
                    emptyMessage="No trainings found."
                    renderItem={(t, i) => {
                        const addr = t.trainingAddress || {};
                        const addressString = [addr.street, addr.apartment, addr.town, addr.county, addr.postalCode, addr.country].filter(Boolean).join(', ');
                        
                        return (
                            <div key={i} style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontWeight: 'bold', color: '#0056b3', textTransform: 'uppercase', fontSize: '18px' }}>
                                        {t.courseTitle || 'TRAINING'}
                                    </div>
                                    <span style={{ padding: '4px 12px', borderRadius: '9999px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                                        UPCOMING
                                    </span>
                                </div>
                                <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f3f4f6' }} />
                                <div style={{ display: 'flex', gap: '16px' }}>
                                    <div style={{ fontSize: '20px' }}>📅</div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontSize: '14px', color: '#1f2937' }}>{t.date || 'Date not specified'}</span>
                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t.timeRange?.from || ''} - {t.timeRange?.to || ''}</span>
                                    </div>
                                </div>
                                <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    📍 {addressString || 'Location not specified'}
                                </div>
                                {t.courseDescription && (
                                    <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                                        <span style={{ fontWeight: 'bold' }}>Description:</span>
                                        <span>{t.courseDescription}</span>
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
            )}

            {activeSubTab === 'clockInLogs' && (
                <PaginatedList
                    title="Clock-in / Clock-out Logs"
                    data={clockInLogs}
                    emptyMessage="No clocking logs found."
                    renderItem={(log, i) => {
                        const start = log.visitStarted ? new Date(log.visitStarted).toLocaleString() : 'N/A';
                        const end = log.visitEnded ? new Date(log.visitEnded).toLocaleString() : 'N/A';
                        
                        return (
                            <div key={log.id || i} style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontWeight: 'bold', color: '#1f2937' }}>
                                        Visit for: {log.serviceUserName || log.serviceUserID || 'Unknown'}
                                    </div>
                                    <span style={{ padding: '4px 12px', borderRadius: '9999px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#eef2ff', color: '#4f46e5' }}>
                                        {log.status || 'LOGGED'}
                                    </span>
                                </div>
                                <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⏱ Clock In: <span style={{ fontWeight: '500', color: '#10b981' }}>{start}</span>
                                </div>
                                <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⏱ Clock Out: <span style={{ fontWeight: '500', color: '#f43f5e' }}>{end}</span>
                                </div>
                                {(log.notes || log.notesForSupervisor) && (
                                    <div style={{ fontSize: '14px', color: '#4b5563', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '8px', marginTop: '4px' }}>
                                        <span style={{ fontWeight: 'bold' }}>Notes:</span> {log.notes || log.notesForSupervisor}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                />
            )}

            {activeSubTab === 'manageShiftTime' && (
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                    <div style={{ fontSize: '48px' }}>⏰</div>
                    <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>Manage Working Hours</h3>
                    <p style={{ color: '#6b7280', textAlign: 'center', maxWidth: '400px', margin: 0 }}>
                        Configure allowed working hours and shift preferences for this employee.
                    </p>
                    <a 
                        href={`/servers/${serverId}/employees/${employee.id}/manage-work-days`}
                        style={{ 
                            padding: '12px 24px', 
                            backgroundColor: '#0056b3', 
                            color: 'white', 
                            borderRadius: '8px', 
                            textDecoration: 'none', 
                            fontWeight: '600',
                            marginTop: '10px',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        Configure Work Days & Hours
                    </a>
                </div>
            )}

            {activeSubTab === 'meetings' && (
                <PaginatedList
                    title="Meetings"
                    data={meetings}
                    emptyMessage="No meetings found."
                    renderItem={(m, i) => (
                        <div key={i} style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 'bold', color: '#0056b3', fontSize: '18px' }}>
                                    {m.title || 'No Title'}
                                </div>
                            </div>
                            {m.subject && <div style={{ fontSize: '14px', color: '#1f2937' }}>{m.subject}</div>}
                            <div style={{ fontSize: '14px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                📅 {m.date || 'N/A'} • {m.startTime || ''} - {m.endTime || ''}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {m.staffID && <span style={{ backgroundColor: '#eef2ff', color: '#4338ca', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '500', border: '1px solid #e0e7ff' }}>👤 Staff: {m.staffID}</span>}
                                {m.adminID && <span style={{ backgroundColor: '#faf5ff', color: '#7e22ce', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '500', border: '1px solid #f3e8ff' }}>🛡️ Admin: {m.adminID}</span>}
                                {m.serviceUserID && <span style={{ backgroundColor: '#f0fdf4', color: '#15803d', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '500', border: '1px solid #dcfce7' }}>👥 Service User: {m.serviceUserID}</span>}
                            </div>
                            {m.virtualMeeting?.link && (
                                <div style={{ marginTop: '8px' }}>
                                    <a href={m.virtualMeeting.link} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'white', backgroundColor: '#0056b3', padding: '8px 16px', borderRadius: '8px', fontWeight: '500', textDecoration: 'none' }}>
                                        🎥 Join Meeting
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                />
            )}

        </div>
    );
}

